import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { Box, Button, IconButton, List, ListItem, ListItemIcon, ListItemSecondaryAction, ListItemText, Typography } from "@mui/material";
import { useDraggable } from "shared-ui/hooks/useDraggable";
import { sortByKey } from "@notemeal/utils-sort";
import { animated } from "react-spring";
import { MenuItemChoiceFormFragment } from "../../../types";
import { useIsFoodFormRedesign } from "../../FoodFormUi/FoodFormUiContext";
import WarningIcon from "../../universal/WarningIcon";
import { menuItemChoiceIsMissingAnyIngredients, menuItemChoiceUsesOldRecipe } from "../utils";

interface MenuItemChoiceListProps {
  choices: readonly MenuItemChoiceFormFragment[];
  onAddChoice: () => void;
  onSelectChoice: (choice: MenuItemChoiceFormFragment) => void;
  onRemoveChoice: (id: string) => void;
  onChangeOrder: (ids: string[]) => void;
}

const legacyChoiceHeight = 61.3;
/** Matches rendered redesign card: mt(8) + box(44) for useDraggable spacing. */
const redesignChoiceHeight = 52;
const redesignChoiceBoxHeight = redesignChoiceHeight - 8;

const MenuItemChoiceList = ({ choices: _choices, onAddChoice, onChangeOrder, onRemoveChoice, onSelectChoice }: MenuItemChoiceListProps) => {
  const isRedesign = useIsFoodFormRedesign();
  const choices = sortByKey(_choices, "position");
  const choiceHeight = isRedesign ? redesignChoiceHeight : legacyChoiceHeight;
  const orderedIds = choices.map(({ id }) => id);
  const draggableChoices = choices.map(({ id }, index) => ({
    id,
    index,
    height: choiceHeight,
  }));
  const { springs, bind } = useDraggable(draggableChoices, onChangeOrder);

  const moveChoice = (id: string, direction: -1 | 1) => {
    const index = orderedIds.indexOf(id);
    const nextIndex = index + direction;
    if (index < 0 || nextIndex < 0 || nextIndex >= orderedIds.length) {
      return;
    }
    const nextIds = [...orderedIds];
    const [moved] = nextIds.splice(index, 1);
    nextIds.splice(nextIndex, 0, moved);
    onChangeOrder(nextIds);
  };

  return (
    <List sx={{ ml: 0, position: "relative", overflowY: "auto", ...(isRedesign ? { py: 0 } : {}) }}>
      {sortByKey(choices, "position").map(c => {
        const springObject = draggableChoices.find(({ id }) => id === c.id);
        if (!springObject) {
          return null;
        }

        const spring = springs[springObject.index];
        const bindProps = bind(springObject.index);

        if (!spring || !bindProps) {
          return null;
        }
        const { zIndex, y, shadow } = spring;

        const missingAnyIngredientsTooltip = menuItemChoiceIsMissingAnyIngredients(c) ? "Some options are missing ingredients" : null;
        const usingOldRecipeTooltip = menuItemChoiceUsesOldRecipe(c) ? "Some options using old recipes" : null;
        const requiredLabel = c.required ? "Required" : "Optional";
        const warningIcon =
          missingAnyIngredientsTooltip || usingOldRecipeTooltip ? (
            <WarningIcon
              tip={
                <>
                  {missingAnyIngredientsTooltip || ""}
                  {missingAnyIngredientsTooltip && usingOldRecipeTooltip && <br />}
                  {usingOldRecipeTooltip || ""}
                </>
              }
            />
          ) : null;

        if (isRedesign) {
          return (
            <Box
              component={animated.div}
              key={c.id}
              style={{
                zIndex,
                boxShadow: shadow.to(s => `rgba(0, 0, 0, 0.15) 0px 0px ${s}px 0px`),
                transform: y.to(y => `translate3d(0, ${y}px, 0)`),
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: 1,
                  px: 1,
                  height: redesignChoiceBoxHeight,
                  boxSizing: "border-box",
                  borderRadius: "6px",
                  bgcolor: "greyscale.100",
                }}
              >
                <IconButton
                  aria-label={`Reorder ${c.name}`}
                  size="small"
                  onKeyDown={event => {
                    if (event.key === "ArrowUp") {
                      event.preventDefault();
                      event.stopPropagation();
                      moveChoice(c.id, -1);
                    } else if (event.key === "ArrowDown") {
                      event.preventDefault();
                      event.stopPropagation();
                      moveChoice(c.id, 1);
                    }
                  }}
                  sx={{ p: 0, color: "greyscale.500", cursor: "grab", flexShrink: 0 }}
                >
                  <DragIndicatorIcon {...bindProps} sx={{ fontSize: 24 }} />
                </IconButton>
                <Box
                  role="button"
                  tabIndex={0}
                  aria-label={`Edit ${c.name}`}
                  onClick={() => onSelectChoice(c)}
                  onKeyDown={event => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onSelectChoice(c);
                    }
                  }}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    cursor: "pointer",
                    "&:focus-visible": {
                      outline: "2px solid",
                      outlineColor: "primary.main",
                      outlineOffset: -2,
                      borderRadius: "4px",
                    },
                  }}
                >
                  <Typography
                    noWrap
                    sx={{
                      fontSize: 14,
                      fontWeight: 500,
                      lineHeight: "20px",
                      color: "text.primary",
                      minWidth: 0,
                    }}
                  >
                    {c.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 400,
                      lineHeight: "20px",
                      color: "text.secondary",
                      flexShrink: 0,
                    }}
                  >
                    {requiredLabel}
                  </Typography>
                </Box>
                {warningIcon}
                <IconButton
                  aria-label="Remove"
                  onClick={() => onRemoveChoice(c.id)}
                  sx={{ p: 0, width: 20, height: 20, flexShrink: 0 }}
                >
                  <CloseIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Box>
            </Box>
          );
        }

        return (
          <animated.div
            key={c.id}
            style={{
              zIndex,
              boxShadow: shadow.to(s => `rgba(0, 0, 0, 0.15) 0px 0px ${s}px 0px`),
              transform: y.to(y => `translate3d(0, ${y}px, 0)`),
            }}
          >
            <ListItem key={c.id} dense button onClick={() => onSelectChoice(c)}>
              <ListItemIcon sx={{ minWidth: 40 }} onClick={e => e.stopPropagation()}>
                <DragIndicatorIcon {...bindProps} />
              </ListItemIcon>
              <ListItemText primary={c.name} secondary={requiredLabel} />
              <ListItemSecondaryAction sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {warningIcon}
                <IconButton edge="end" onClick={() => onRemoveChoice(c.id)} size="large">
                  <CloseIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          </animated.div>
        );
      })}
      <ListItem sx={{ p: 0, ...(isRedesign ? { mt: 1 } : {}) }}>
        <Button variant="outlined" sx={{ flexGrow: 1 }} onClick={onAddChoice}>
          New Add-on
        </Button>
      </ListItem>
    </List>
  );
};

export default MenuItemChoiceList;
